import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { ToggleViewIdeaButton } from '@doltech/ui/lib/shared/components/IELTSample/shared/plugins/ToggleViewIdeaButton';
import { AudioTypeEnum } from '@doltech/utils/lib/constants';
import { wordCount } from '@doltech/utils/lib/sentence';
import cl from 'classnames';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useInView } from 'react-cool-inview';
import styled from 'styled-components';

import {
  getInitialIdeaFlow,
  getInitialVocabData,
  getParagraphNodeTextWithoutTitle,
  getTextFromIdeaVocabFlow,
} from '../../sample.utils';
import { TitleH2Block } from '../../Writing/Steps/SamplePreview';
import { SkeletonCompsSingleLine } from '@doltech/ui/lib/figma/SkeletonComps/SingleLine/SkeletonCompsSingleLine';
import { ViewVocabIdeaEditorReadOnly } from '@doltech/ui/lib/shared/components/IELTSample/shared/ViewVocabIdeaEditorReadOnly';

const AudioLoader = () => <SkeletonCompsSingleLine maxWidth="350px" fill />;

const AudioPlayerForEditor = dynamic<any>(
  () =>
    import('@doltech/ui/lib/figma/Audio/Player/AudioPlayerForEditor').then(
      (mod) => mod.AudioPlayerForEditor as any
    ),
  {
    loading: () => <AudioLoader />,
    ssr: false,
  }
);

const AudioPlayerForEditorBase = dynamic<any>(
  () =>
    import('@doltech/ui/lib/figma/Audio/Player/AudioPlayerForEditor').then(
      (mod) => mod.AudioPlayerForEditorBase as any
    ),
  {
    loading: () => <AudioLoader />,
    ssr: false,
  }
);

const Main = styled.div`
  display: grid;
  gap: 24px;
  .flow-vocab-idea-title {
    display: grid;

    align-items: center;
    gap: 8px;

    @media ${deviceBreakpoints.fromTablet} {
      grid-auto-flow: column;
      gap: 16px;
      grid-auto-columns: minmax(0, max-content);
    }
  }

  .view-idea-enabled {
    color: ${colorsV2.gray60};
  }

  .vocab-paragraphs {
    display: grid;
    gap: 24px;
  }
`;

export const FlowVocabIdeaEditor = ({
  editorId,
  vocabContent,
  ideaContent,
  ideaEnabled,
  withTitleNode,
  audio,
  paragraphIndex,
  className = undefined,
}) => {
  return (
    <ViewVocabIdeaEditorReadOnly
      renderElementDeps={[ideaEnabled, editorId, withTitleNode, audio, paragraphIndex]}
      isReadOnly
      key={ideaEnabled + editorId}
      className={cl('flow-vocab-idea-editor', { 'view-idea-enabled': ideaEnabled }, className)}
      initialValue={ideaEnabled ? ideaContent : vocabContent}
      withTitleNode={withTitleNode}
      audio={audio}
      paragraphIndex={paragraphIndex}
    />
  );
};

interface FlowVocabIdeaPreviewProps {
  title: string | JSX.Element;
  detailData: any;
  showIdeaButton?: any;
  withTitleNode?: boolean;
  audioMode?: 'SINGLE' | 'MULTIPLE';
  renderDescription?: () => JSX.Element;
}

export const FlowVocabIdeaPreview = ({
  title,
  detailData,
  showIdeaButton = true,
  withTitleNode,
  audioMode = 'SINGLE',
  renderDescription = () => null,
}: FlowVocabIdeaPreviewProps) => {
  const [ideaEnabled, setIdeaEnabled] = React.useState(false);
  const [ideaParagraphs, vocabParagraphs] = React.useMemo(() => {
    return [
      getInitialIdeaFlow(detailData, withTitleNode, true),
      getInitialVocabData(detailData, withTitleNode),
    ];
  }, [detailData, withTitleNode]);

  const vocabIdeaText = React.useMemo(() => getTextFromIdeaVocabFlow(detailData), [detailData]);

  const renderSingleAudio = React.useCallback(() => {
    if (audioMode === 'SINGLE') {
      const foundAudio = detailData?.audios?.[0];
      if (foundAudio?.type === AudioTypeEnum.UPLOAD && foundAudio?.audio?.url) {
        return <AudioPlayerForEditorBase url={foundAudio.audio.url} />;
      }
      return <AudioPlayerForEditor text={vocabIdeaText} />;
    }
    return null;
  }, [audioMode, detailData?.audios, vocabIdeaText]);

  const getAudioMultiple = React.useCallback(
    (id, content) => {
      if (audioMode === 'MULTIPLE') {
        const foundAudio = detailData?.audios?.find((file) => file.id === id);
        if (foundAudio?.type === AudioTypeEnum.UPLOAD && foundAudio?.audio?.url) {
          return {
            type: foundAudio.type,
            url: foundAudio.audio.url,
          };
        }
        return {
          type: AudioTypeEnum.GOOGLE,
          text: getParagraphNodeTextWithoutTitle(content),
        };
      }
      return null;
    },
    [audioMode, detailData?.audios]
  );

  const { observe, inView } = useInView({
    unobserveOnEnter: true,
    rootMargin: '50px',
  });

  return (
    <Main>
      <div ref={observe} className="flow-vocab-idea-title">
        <TitleH2Block>{title}</TitleH2Block>
        {inView && renderSingleAudio()}
      </div>
      {renderDescription()}
      {showIdeaButton && (
        <ToggleViewIdeaButton ideaEnabled={ideaEnabled} setIdeaEnabled={setIdeaEnabled} />
      )}
      <div className="vocab-paragraphs">
        {vocabParagraphs.map((vocabParagraph, vocabParagraphIndex) => {
          const { id, content } = vocabParagraph;
          const ideaNode = ideaParagraphs[vocabParagraphIndex];
          return (
            <FlowVocabIdeaEditor
              key={id}
              ideaContent={ideaNode?.content}
              audio={getAudioMultiple(id, content)}
              ideaEnabled={ideaEnabled}
              editorId={id}
              vocabContent={content}
              withTitleNode={withTitleNode}
              paragraphIndex={vocabParagraphIndex + 1}
            />
          );
        })}
      </div>
      <Typography.Paragraph color="black80" className="font-noto" variant="regular/20">
        ({wordCount(vocabIdeaText) || 0} words)
      </Typography.Paragraph>
    </Main>
  );
};
